/**
 * Microphone - Types, Constants, and Utilities
 *
 * This module contains:
 * - Types for microphone permission and device management
 * - Constants (approved microphones, timeouts)
 * - Error classes for permission flows
 * - Utility functions (deduplication, timeout wrapper)
 *
 * All browser API interaction (permissions, device enumeration, getUserMedia)
 * lives in the useMicrophone hook. This module provides the shared types
 * and utilities that the hook and consumers need.
 *
 * Note: hasAudioActivityAtom lives in atoms/recording.ts since it's a
 * recording-readiness concern (written by VoiceCard, read by useMicrophone).
 */

// ============================================================================
// Constants
// ============================================================================

/** Timeout for getUserMedia permission request (30 seconds) */
export const PERMISSION_REQUEST_TIMEOUT_MS = 30_000;

/**
 * List of approved microphone names for auto-selection.
 * Matching is case-insensitive and uses partial matching (includes).
 * If an available device label contains any of these names,
 * it will be auto-selected as the preferred microphone.
 *
 * @example
 * // "TONOR G11 USB Microphone" matches "TONOR G11"
 * // "hollyland mark m2" matches "Hollyland Mark M2"
 */
export const APPROVED_MICROPHONES = ['TONOR G11', 'Hollyland Mark M2'];

/**
 * Labels containing this (case-insensitive) are never auto-selected as preferred.
 * Used to skip "Communications - Microphone (...)" and similar virtual endpoints.
 */
export const COMMUNICATIONS_LABEL_MARKER = 'communications';

// ============================================================================
// Types
// ============================================================================

export type MicrophonePermissionState =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unknown';

/**
 * Result of requesting microphone permission.
 * Use reason to distinguish actual user denial from timeout, prompt-blocked, or hardware errors.
 */
export type MicrophonePermissionRequestResult =
  | { granted: true }
  | {
      granted: false;
      reason: 'denied' | 'timeout' | 'prompt-blocked' | 'error';
    };

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

// ============================================================================
// Error Classes
// ============================================================================

/**
 * Error thrown when getUserMedia times out.
 * This can happen when the user ignores the permission prompt
 * or when macOS system permissions block the browser prompt.
 */
export class MicrophonePermissionTimeoutError extends Error {
  constructor() {
    super(
      'Microphone permission request timed out. ' +
        'Please check your browser and system microphone permissions.',
    );
    this.name = 'MicrophonePermissionTimeoutError';
  }
}

/**
 * Error thrown when the browser won't show the permission prompt.
 * This can happen when:
 * - The user previously dismissed the prompt without making a choice
 * - The browser has rate-limited permission requests
 * - The page needs to be refreshed for Chrome to show the prompt again
 */
export class MicrophonePermissionPromptBlockedError extends Error {
  constructor() {
    super(
      'The browser is not showing the microphone permission prompt. ' +
        'Please refresh the page and try again, or enable microphone access in your browser settings.',
    );
    this.name = 'MicrophonePermissionPromptBlockedError';
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Find the first device whose label matches an approved microphone name.
 * Matching is case-insensitive and uses partial matching (includes).
 */
export function findApprovedDevice(
  devices: AudioDevice[],
): AudioDevice | undefined {
  return devices.find((device) =>
    APPROVED_MICROPHONES.some((approvedName) =>
      device.label.toLowerCase().includes(approvedName.toLowerCase()),
    ),
  );
}

/**
 * Wraps a promise with a timeout.
 *
 * @throws {MicrophonePermissionTimeoutError} if the timeout is reached
 */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new MicrophonePermissionTimeoutError()), ms),
    ),
  ]);
}

/**
 * Build a MediaTrackConstraints object for a given deviceId.
 *
 * Uses `exact` so getUserMedia always targets the precise device the user
 * selected. If the device is unavailable the browser will throw an
 * OverconstrainedError — which callers can surface as a clear error —
 * rather than silently falling back to a different mic (which `ideal` does).
 *
 * IMPORTANT: Both monitoring (MicrophoneSelector) and recording
 * (AudioRecorderProvider / useMediaRecorder) MUST use this function so
 * the two streams always target the same device. A mismatch caused a bug
 * where monitoring showed "mic working" on a fallback device while
 * recording actually failed on the selected device.
 *
 * @param deviceId - The selected device ID, or null/undefined for the
 *   browser default.
 * @param extras - Additional MediaTrackConstraints to merge
 *   (e.g. echoCancellation, noiseSuppression). Defaults to `{}`.
 * @returns `true` (default device) or a MediaTrackConstraints object
 *   containing `{ deviceId: { exact } }` plus any extras.
 */
export function buildAudioConstraints(
  deviceId: string | null | undefined,
  extras: Omit<MediaTrackConstraints, 'deviceId'> = {},
): MediaTrackConstraints | true {
  const hasExtras = Object.keys(extras).length > 0;

  if (!deviceId) {
    return hasExtras ? { ...extras } : true;
  }

  return {
    deviceId: { exact: deviceId },
    ...extras,
  };
}

/** Dedupe by groupId (same device); prefer non-empty label, then shorter deviceId. */
export const deduplicateAudioDevices = (
  devices: MediaDeviceInfo[],
): MediaDeviceInfo[] =>
  Array.from(
    devices
      // One entry per physical device (keyed by groupId or deviceId)
      .reduce((uniqueDevices, device) => {
        const key = device.groupId || device.deviceId; // same groupId = same physical device
        if (!uniqueDevices.has(key)) {
          uniqueDevices.set(key, device);
        } else {
          const existing = uniqueDevices.get(key) ?? device;
          const hasNewLabel = device.label?.trim();
          const hasExistingLabel = existing.label?.trim();
          // Prefer entry with label, or shorter deviceId if both have labels
          if (!hasExistingLabel && hasNewLabel) {
            uniqueDevices.set(key, device);
          } else if (
            hasExistingLabel &&
            hasNewLabel &&
            device.deviceId.length < existing.deviceId.length
          ) {
            uniqueDevices.set(key, device);
          }
        }
        return uniqueDevices;
      }, new Map<string, MediaDeviceInfo>())
      .values(),
  );
